import React, { useEffect, useState } from "react";
import { Spinner } from "reactstrap";
import { observer, inject } from "mobx-react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

import { postVariants } from "../../components/util";
import {
  buildStudioPlusCheckoutRetryUrl,
  isStudioPlusPaymentsSuccessStatus,
  pollStudioPlusPaymentStatus,
} from "../../components/payments/studioPlusPayments";

const PROCESSING_STATUS = "processing";

const PaymentReturnPage: React.FC<any> = ({
  authStore,
  navigationStore,
  ecomStore,
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState("");

  useEffect(() => {
    // If onCheckout was already called (e.g. page refresh after success),
    // go straight to confirmation.
    if (ecomStore?.successfulOrder?.Key && !ecomStore?.cart?.Key) {
      navigationStore.push("/checkout/confirmation");
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const paymentSessionId = params.get("paymentSessionId");

    if (!paymentSessionId) {
      setError("Missing payment information. Please return to checkout and try again.");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const result = await pollStudioPlusPaymentStatus(paymentSessionId);

        if (cancelled) {
          return;
        }

        const status = result?.Status?.toLowerCase();
        if (isStudioPlusPaymentsSuccessStatus(result?.Status) || status === PROCESSING_STATUS) {
          ecomStore.onCheckout();
          navigationStore.push("/checkout/confirmation");
        } else {
          navigationStore.replace(buildStudioPlusCheckoutRetryUrl());
          return;
        }
      } catch {
        if (!cancelled) {
          setError("We could not verify the payment. Please try again.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ecomStore, navigationStore]);

  return (
    <>
      <Helmet>
        <title>
          {t("PaymentPage.urlTitle")}{" "}
          {authStore.companyName || authStore.companyName !== ""
            ? "| " + authStore.companyName
            : " "}
        </title>
      </Helmet>
      <motion.div
        initial="initial"
        animate="enter"
        exit="exit"
        variants={postVariants}
      >
        <div className="text-center py-5">
          {!error ? (
            <>
              <Spinner color="primary" />
              <p className="mt-3">Verifying your payment...</p>
            </>
          ) : (
            <>
              <p className="text-danger mb-3">{error}</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigationStore.push("/checkout/payment")}
              >
                Return to payment
              </button>
            </>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default inject(
  "authStore",
  "navigationStore",
  "ecomStore"
)(observer(PaymentReturnPage));
